// @flow
import SMConst from 'frontend_core/dist/constants/sm-const'
import Path from 'routes/path'
import { getFieldErrorMessages, showErrorMessage } from 'helpers/error'
import { type State as ReservationDetailState } from 'redux/modules/reservationDetail'
import {
  actions as ReservationEditActions,
  type State as ReservationEditState,
} from 'redux/modules/reservationEdit'
import { getBillStatementPropsForEdit } from 'components/molecules/BillStatement/presenter'
import { getReservationGetPointBlockProps } from 'components/molecules/ReservationGetPointBlock/presenter'
import { getReservationAdditionalFeeBlockProps } from 'components/molecules/ReservationAdditionalFeeBlock/presenter'

export function getReservationEditFormProps(
  userId: string,
  reservationDetail: ReservationDetailState,
  reservationEdit: ReservationEditState,
  actions: ReservationEditActions,
  onClickBackToMessage: Function,
  onClickSend: Function,
) {
  const { detail } = reservationDetail
  const selectedPlan = reservationEdit.plan || {}
  const unapproved = reservationEdit.unapprovedReservation
  const unapprovedBilling = unapproved.billing || {}

  const currentRoom = detail.room || {}
  const plans = (currentRoom.plans || [])
    .filter(p => p.status === SMConst.Visibility.Visible)
    .map(p => {
      const priceUnits = []

      if (p.has_hourly_price) {
        priceUnits.push({
          price: p.hourly_price_text,
          unit: p.hourly_price_unit_text || '',
        })
      }
      if (p.has_session_price) {
        priceUnits.push({
          price: p.session_price_text,
          unit: p.session_price_unit_text || '',
        })
      }
      if (p.has_daily_price) {
        priceUnits.push({
          price: p.daily_price_text,
          unit: p.daily_price_unit_text || '',
        })
      }
      if (p.has_stay_price) {
        priceUnits.push({
          price: p.stay_price_text,
          unit: p.stay_price_unit_text || '',
        })
      }

      return {
        name: p.name,
        priceUnits: priceUnits,
      }
    })

  const rentTypes = []
  if (selectedPlan.has_hourly_price && selectedPlan.has_daily_price) {
    rentTypes.push(
      {
        label: i18n.t('page.dashboard.reservation.rentTime'),
        value: 0,
      },
      {
        label: i18n.t('page.dashboard.reservation.rentDay'),
        value: 1,
      },
    )
  }

  const roomOptions = currentRoom.option_items || []
  const optionItems = roomOptions
    .filter(option => option.status === SMConst.Visibility.Visible)
    .map(option => {
      const reservedOption = reservationEdit.optionItems.find(
        reservedOption => reservedOption.option_item_id === option.id,
      )
      return {
        id: option.id,
        name: option.name,
        price: option.price_text || '',
        unit: option.price_unit_text || '',
        checked: !!reservedOption,
        count: (reservedOption || {}).number || 1,
        countList: option.countable
          ? Array(option.max_count || 0)
              .fill(0)
              .map((_, index) => ({
                label: `${index + 1}`,
                value: index + 1,
              }))
          : null,
      }
    })

  const billStatementProps = getBillStatementPropsForEdit(
    reservationDetail,
    reservationEdit,
  )

  return {
    plan: {
      selectedIndex: (currentRoom.plans || []).findIndex(
        plan => plan.id === selectedPlan.id,
      ),
      onChange: e =>
        actions.editReservationDryrun({
          userId,
          plan: (currentRoom.plans || [])[e.target.value],
        }),
      list: plans,
    },
    dateTime: {
      selectedRentType: reservationEdit.isHourly ? 0 : 1,
      rentTypes,
      startDate: reservationEdit.startedAt,
      endDate: reservationEdit.endedAt,
      isHourly: reservationEdit.isHourly,
      isStay: detail.rent_type === SMConst.RentType.Stay,
      startHour: reservationEdit.startHour,
      endHour: reservationEdit.endHour,
      hourList: Array.from({ length: 24 }, (_, i) => ({
        label: `${i}:00`,
        value: i,
      })),
      hrefCheckAvailability: Path.roomReservationRequest(
        (currentRoom.space || {}).username,
        currentRoom.uid,
      ),
      onChangeRentType: (e: Event) =>
        // ここで選択されるのは時間単位と1日単位の場合のみ
        actions.editReservationDryrun({
          userId,
          isHourly: e.target.value === 0,
        }),
      onChangeStartDate: (date: moment) =>
        actions.editReservationDryrun({
          userId,
          startedAt: date,
        }),
      onChangeEndDate: (date: moment) =>
        actions.editReservationDryrun({
          userId,
          endedAt: date,
        }),
      onChangeStartHour: (value: number) =>
        actions.editReservationDryrun({
          userId,
          startHour: value,
        }),
      onChangeEndHour: (value: number) =>
        actions.editReservationDryrun({
          userId,
          endHour: value,
        }),
      errorTexts: getFieldErrorMessages(
        reservationEdit.failedEditReservationDryrun,
        'schedules',
      ),
    },
    option: {
      list: optionItems,
      onChangeChecked: (e: Event, index: number) => {
        let updateOptionItems = [].concat(reservationEdit.optionItems)
        if (e.target.checked) {
          updateOptionItems.push({ option_item_id: roomOptions[index].id })
        } else {
          updateOptionItems = updateOptionItems.filter(
            option => option.option_item_id !== roomOptions[index].id,
          )
        }
        actions.editReservationDryrun({
          userId,
          optionItems: updateOptionItems,
        })
      },
      onChangeCount: (value: number, index: number) => {
        let updateOptionItems = [].concat(reservationEdit.optionItems)
        updateOptionItems = updateOptionItems.map(option => ({
          ...option,
          number:
            option.option_item_id === roomOptions[index].id
              ? value
              : option.count,
        }))
        actions.editReservationDryrun({
          userId,
          optionItems: updateOptionItems,
        })
      },
    },
    reservationGetPointBlockProps: getReservationGetPointBlockProps(
      detail,
      unapproved,
    ),
    billStatementProps,
    reservationAdditionalFeeBlockProps: getReservationAdditionalFeeBlockProps(
      detail,
      billStatementProps.additionalFee,
      unapproved,
    ),
    onClickBackToMessage: onClickBackToMessage,
    onClickSend: onClickSend,
    isSendButtonDisabled:
      !reservationEdit.hasChanged || reservationEdit.failedEditReservation,
  }
}



// WEBPACK FOOTER //
// ./src/components/organisms/ReservationEditForm/presenter.js